import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { subscriptionAPI } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const BillingPage: React.FC = () => {
  useDocumentTitle('Billing & Subscription');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [showActivatedBanner, setShowActivatedBanner] = useState(false);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const subscriptionActivated = searchParams.get('subscription') === 'activated';

    if (subscriptionActivated) {
      setShowActivatedBanner(true);
      sessionStorage.removeItem('payment_pending');
      sessionStorage.removeItem('payment_pending_plan');
      sessionStorage.removeItem('payment_pending_period');
      navigate('/billing', { replace: true });
    }

    if (paymentStatus === 'complete') {
      sessionStorage.removeItem('payment_pending');
      sessionStorage.removeItem('payment_pending_plan');
      sessionStorage.removeItem('payment_pending_period');
      toast.success('Payment completed! Your subscription is being activated.');
      navigate('/billing', { replace: true });
      return;
    }
    if (paymentStatus === 'canceled') {
      toast.error('Payment was canceled.');
      navigate('/billing/plans', { replace: true });
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await subscriptionAPI.getCurrent();
      setData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will be downgraded to the Free plan.')) return;
    try {
      await subscriptionAPI.cancel('Canceled from billing page');
      toast.success('Subscription canceled');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to cancel');
    }
  };

  const handleRemovePaymentMethod = async (id: number) => {
    if (!window.confirm('Remove this payment method?')) return;
    try {
      await subscriptionAPI.removePaymentMethod(id);
      toast.success('Payment method removed');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to remove');
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await subscriptionAPI.setDefaultPaymentMethod(id);
      toast.success('Default payment method updated');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-success', paid: 'bg-success', trialing: 'bg-info',
      past_due: 'bg-warning', canceled: 'bg-secondary', expired: 'bg-danger', free: 'bg-secondary',
    };
    return <span className={`badge ${colors[status] || 'bg-secondary'}`}>{status}</span>;
  };

  if (loading) {
    return <DashboardLayout><div className="d-flex justify-content-center py-5"><LoadingSpinner /></div></DashboardLayout>;
  }

  const sub = data?.subscription;

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {showActivatedBanner && (
          <div className="alert alert-success d-flex align-items-center gap-3 mb-4 shadow-sm border-0" style={{ borderRadius: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="bi bi-check-lg text-white" style={{ fontSize: 22 }}></i>
            </div>
            <div className="flex-grow-1">
              <div className="fw-bold">Subscription Activated!</div>
              <div className="small">Your payment was successful. Welcome aboard — your plan is now active.</div>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-success" onClick={() => navigate('/analytics/summary')}>
                <i className="bi bi-speedometer2 me-1"></i>Go to Dashboard
              </button>
              <button className="btn-close" onClick={() => setShowActivatedBanner(false)}></button>
            </div>
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="h3 fw-bold mb-1">Billing & Subscription</h2>
            <p className="text-muted mb-0">Manage your plan, payment methods, and billing history</p>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/billing/plans')}>
            <i className="bi bi-arrow-up-circle me-2"></i>
            {sub ? 'Change Plan' : 'View Plans'}
          </button>
        </div>

        <div className="row g-4">
          {/* Current Plan */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <h5 className="card-title mb-3">Current Plan</h5>
                {sub ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h3 className="fw-bold mb-1">{sub.plan_name}</h3>
                        <p className="text-muted mb-0">
                          {sub.currency} {Number(sub.amount || 0).toFixed(2)} / {sub.billing_period}
                        </p>
                      </div>
                      {getStatusBadge(sub.status)}
                    </div>
                    <div className="row g-3">
                      <div className="col-sm-4">
                        <small className="text-muted d-block">Billing Period</small>
                        <span className="fw-medium text-capitalize">{sub.billing_period}</span>
                      </div>
                      <div className="col-sm-4">
                        <small className="text-muted d-block">Started</small>
                        <span className="fw-medium">{sub.starts_at ? new Date(sub.starts_at).toLocaleDateString() : '-'}</span>
                      </div>
                      <div className="col-sm-4">
                        <small className="text-muted d-block">Next Renewal</small>
                        <span className="fw-medium">{sub.ends_at ? new Date(sub.ends_at).toLocaleDateString() : '-'}</span>
                      </div>
                    </div>
                    {sub.status !== 'canceled' && sub.amount > 0 && (
                      <div className="mt-3 pt-3 border-top">
                        <button className="btn btn-outline-danger btn-sm" onClick={handleCancel}>Cancel Subscription</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bi bi-box-seam text-muted" style={{ fontSize: '3rem' }}></i>
                    <h5 className="mt-3">Free Plan</h5>
                    <p className="text-muted">Upgrade to unlock more features and higher limits.</p>
                    <button className="btn btn-primary" onClick={() => navigate('/billing/plans')}>View Plans</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">Payment Methods</h5>
                  <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/billing/checkout?action=add-method')}>
                    <i className="bi bi-plus"></i> Add
                  </button>
                </div>
                {data?.payment_methods?.length > 0 ? (
                  <div className="list-group list-group-flush">
                    {data.payment_methods.map((pm: any) => (
                      <div key={pm.id} className="list-group-item px-0 d-flex justify-content-between align-items-center">
                        <div>
                          <i className={`bi ${pm.type === 'paypal' ? 'bi-paypal' : 'bi-credit-card'} me-2`}></i>
                          <span className="text-capitalize">{pm.brand || pm.type}</span>
                          {pm.last_four && <span className="text-muted ms-1">****{pm.last_four}</span>}
                          {pm.is_default ? <span className="badge bg-primary ms-2">Default</span> : null}
                        </div>
                        <div className="dropdown">
                          <button className="btn btn-sm btn-link text-muted p-0" data-bs-toggle="dropdown">
                            <i className="bi bi-three-dots-vertical"></i>
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            {!pm.is_default && <li><button className="dropdown-item" onClick={() => handleSetDefault(pm.id)}>Set as Default</button></li>}
                            <li><button className="dropdown-item text-danger" onClick={() => handleRemovePaymentMethod(pm.id)}>Remove</button></li>
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted small mb-0">No payment methods added yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Payments */}
        <div className="card border-0 shadow-sm mt-4">
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0">Recent Payments</h5>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/billing/payments')}>View All</button>
            </div>
            {data?.recent_payments?.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_payments.map((p: any) => (
                      <tr key={p.id}>
                        <td>{new Date(p.paid_at || p.created_at).toLocaleDateString()}</td>
                        <td className="text-capitalize">{p.payment_type}</td>
                        <td>{p.currency} {Number(p.amount || 0).toFixed(2)}</td>
                        <td>{getStatusBadge(p.status)}</td>
                        <td>
                          {p.status === 'succeeded' && (
                            <button className="btn btn-link btn-sm p-0" onClick={() => navigate(`/billing/receipt/${p.id}`)}>
                              <i className="bi bi-receipt me-1"></i>View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted small mb-0">No payments yet.</p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BillingPage;
